import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, Wrench, Shield, Monitor, Settings2, ArrowRight } from 'lucide-react';

const NotificationCenter = () => {
  const notificationTypes = [
    {
      id: 'renewals',
      title: 'Renewal Alerts',
      description: 'Never miss contract renewals with intelligent alert systems.',
      icon: <AlertTriangle size={24} />,
      path: '/notifications/renewals',
      color: 'bg-red-50 text-red-600'
    },
    {
      id: 'maintenance',
      title: 'Maintenance Alerts',
      description: 'Stay on top of asset maintenance schedules and requirements.',
      icon: <Wrench size={24} />,
      path: '/notifications/maintenance',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      id: 'compliance',
      title: 'Compliance Alerts',
      description: 'Ensure regulatory compliance with automated monitoring.',
      icon: <Shield size={24} />,
      path: '/notifications/compliance',
      color: 'bg-green-50 text-green-600'
    },
    {
      id: 'system',
      title: 'System Alerts',
      description: 'Monitor system health and performance with real-time alerts.',
      icon: <Monitor size={24} />,
      path: '/notifications/system',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      id: 'settings',
      title: 'Notification Settings',
      description: 'Customize your notification preferences and delivery methods.',
      icon: <Settings2 size={24} />,
      path: '/notifications/settings',
      color: 'bg-gray-50 text-gray-600'
    }
  ];

  return (
    <div className="notification-center" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-2xl">
                <Bell size={48} />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Notification Center</h1>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Centralized hub for managing all your notifications and alerts.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Notification Types</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive notification management for all your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {notificationTypes.map((notification) => (
              <Link
                key={notification.id}
                to={notification.path}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-orange-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${notification.color}`}>
                    {notification.icon}
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="text-gray-400 group-hover:text-orange-600 transition-colors" 
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                  {notification.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {notification.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Stay Informed and Proactive
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Configure intelligent alerts that keep you informed without overwhelming you.
          </p>
          <Link
            to="/notifications/settings"
            className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Settings2 size={20} className="mr-2" />
            Configure Settings
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
